/**
 * The gap between "the pack was accepted" and "the chat has something to show for it".
 *
 * Accepting a pack does not produce a reply — it dispatches a hidden SYSTEM message into a freshly
 * created conversation and returns. The maestro's walkthrough of the pack is a whole agentic turn
 * behind that: measured in production on 2026-09-14, `pendingPredictionId` was stamped ~2.2s after the
 * accept and the first words landed 27s after it. The SYSTEM row is invisible to clients, so for those
 * first seconds the chat has no messages, no stamp, and therefore nothing to render at all.
 *
 * That is what the accept landing dropped people into: a blank screen, reported as "it opens a chat and
 * shows nothing". So the URL carries the marker below, and seeing it arms a local busy state — the
 * thinking loader shows from the first frame, and the real turn takes over the moment the conversation
 * carries a stamp or a row streams in. The accept forwards into that chat with nobody tapping anything,
 * so a chat that opened blank would be the whole experience.
 *
 * Same shape and the same reasoning as `lib/connect-handoff`, one flow over.
 */

/**
 * The accept landing stamps this on the chat URL it opens.
 *
 * ⚠️ It is consumed on arrival, and consuming it means dropping BOTH copies — the address and the Next
 * router's query. `lib/use-pack-handoff` does that; read the comment there before changing either write.
 * A copy left in `router.query` is put straight back on the address by any writer that rebuilds a chat URL
 * from `...router.query`, and reloading or sharing that URL arms "Adding this pack…" for the full grace
 * window on a chat where nothing is coming. The chat-URL writers drop it themselves as well, so a writer
 * that runs in the same tick as the arrival cannot carry it forward ahead of that strip.
 */
export const PACK_HANDOFF_PARAM = 'packPending';

/**
 * How long the accept alone is allowed to mean "busy".
 *
 * It bridges to a real signal, it does not replace one: an announcement that never comes (the receiver
 * was brand-new so their pack intro rides the onboarding greeting instead, the dispatch was dropped,
 * the service is down) must not leave a spinner running forever. Wider than the connect handoff's 45s
 * because the wait it covers is genuinely longer — the observed reply took 27s and that turn had to
 * read the pack manifest, the receipt and the receiving-packs skill before it could say anything.
 */
export const PACK_HANDOFF_GRACE_MS = 90 * 1000;

/** Whether an accept observed at `armedAt` still counts as a turn about to start. */
export function packHandoffPending(armedAt: number | null, now: number = Date.now()): boolean {
  if (armedAt === null) return false;
  // A clock-skewed future stamp is still live — only the past can expire.
  return now - armedAt < PACK_HANDOFF_GRACE_MS;
}

/**
 * A canned onboarding greeting is not the pack walkthrough's answer. Bridge to its server turn,
 * then follow that turn until it settles. An idle prepared card needs no bridge. The marker expires via
 * `packHandoffPending`; Stop and an error must end this bridge immediately as well.
 */
export function packWalkthroughPending({
  handoffPending,
  turnSeen,
  turnActive,
  hasCard,
  stopped,
  failed,
  settled = false,
  userTurnStarted = false,
}: {
  handoffPending: boolean;
  turnSeen: boolean;
  turnActive: boolean;
  hasCard: boolean;
  stopped: boolean;
  failed: boolean;
  settled?: boolean;
  userTurnStarted?: boolean;
}): boolean {
  // A prepared, idle chat is already ready. During a live turn an older card in the transcript says
  // nothing about this arrival, so keep following the turn rather than mistaking that card for its reply.
  return handoffPending && !settled && !userTurnStarted && !stopped && !failed && (!hasCard || turnActive) && (!turnSeen || turnActive);
}

/**
 * A prompt the invite link asked the chat to open with — the same `prefill` / `autosend` a plain
 * `/?prefill=…&autosend=1` link carries. Wix surfaces that hand a merchant a pack (the Business Manager
 * entry, `@wix/symphony-packs-kit`) put it on the invite so the prompt survives the accept.
 *
 * `new` is deliberately not part of it: the accept already chose the conversation (the fresh one the
 * announcement lands in), and `new=1` would override that and open an empty thread instead.
 */
export interface PackChatPrompt {
  prefill?: string;
  autosend?: boolean;
}

/** Reads the prompt off the accept landing's query; strings only, anything else is ignored. */
export function packChatPromptFromQuery(query: Record<string, string | string[] | undefined>): PackChatPrompt {
  const prefill = typeof query.prefill === 'string' && query.prefill !== '' ? query.prefill : undefined;
  return {
    ...(prefill !== undefined ? { prefill } : {}),
    ...(prefill !== undefined && query.autosend === '1' ? { autosend: true } : {}),
  };
}

/**
 * Whether an accepted pack may carry its invite's prompt into the chat.
 *
 * - Only a pack Wix sent (the Business Manager entry): a prompt on a pack one user shared with another
 *   would let the sharer make the recipient's agent act on words the recipient never typed.
 * - Only with a conversation to land in: a brand-new recipient gets no id, and their pack intro rides
 *   the onboarding kickoff, which an autosend would pre-empt.
 */
export function packPromptAllowed(outcome: { fromWix: boolean; conversationId?: string }): boolean {
  return outcome.fromWix && Boolean(outcome.conversationId);
}

/**
 * The prompt an AUTOMATIC forward may carry: the prefill, never the autosend.
 *
 * `prefill` and `autosend` are read off the LANDING URL, so they are whatever the person who handed out
 * the link typed there — `packPromptAllowed` only establishes that the PACK came from Wix, and a Wix
 * invite link with `&prefill=…&autosend=1` appended to it still satisfies that. While an "Open chat"
 * button stood here, a send at least took a tap on the destination; the forward removed it, which would
 * let anyone holding a Wix pack link make a recipient's agent act on text the recipient never typed,
 * with the recipient's own permissions.
 *
 * So the words still ride — they land in the composer, where they are visible and abandonable — and the
 * send stays the reader's. Binding a prompt to the server-issued pack (so a genuine Wix prompt could
 * send itself again) is a harness change: the accept response would have to carry the prompt it approves.
 */
export function packForwardPrompt(prompt: PackChatPrompt): PackChatPrompt {
  return prompt.prefill ? { prefill: prompt.prefill } : {};
}

/** The chat URL the accept landing forwards into, marked so the chat knows a turn is on its way. */
export function packChatHref(conversationId: string | undefined, prompt: PackChatPrompt = {}): string {
  const params = new URLSearchParams();
  if (conversationId) params.set('conversation', conversationId);
  // Fullscreen, never bare `/`: `/` only opens the chat for a user still in onboarding state.
  params.set('chat', 'fullscreen');
  params.set(PACK_HANDOFF_PARAM, '1');
  // The autosend waits for the announcement turn before it sends (see `useAutosend`'s `turnInFlight`),
  // so the prompt lands AFTER the pack walkthrough instead of being refused mid-turn.
  if (prompt.prefill) params.set('prefill', prompt.prefill);
  if (prompt.prefill && prompt.autosend) params.set('autosend', '1');
  return `/?${params.toString()}`;
}
